import type { AccessHistoryItem, HistoryDetailGroup, HistoryKind, HistoryWho } from './types';
import { actionText, dateText } from './format';

/**
 * Words and grouping of the Access history page (owner 2026-10-19: "build it and make it more
 * beautiful"). The server writes the sentence, summary and details with names (identity
 * access-history readable.ts); this file only labels the kinds, groups the rows by day and
 * works out the period filter. No ids are ever shown.
 */

export const KIND_LABEL: Record<HistoryKind, string> = {
  role: 'Role',
  role_switch: 'Role switch',
  joined: 'Joined',
  left: 'Left',
  invitation: 'Invitation',
  approval: 'Approval',
  permission: 'Permission',
  organisation: 'Organisation set-up',
  account: 'Account',
};

/** Tag colours per kind (the same soft fills as the type badges of All Users). */
export const KIND_STYLE: Record<HistoryKind, { fg: string; bg: string }> = {
  role: { fg: '#3730a3', bg: '#e0e7ff' },
  role_switch: { fg: '#1d4ed8', bg: '#dbeafe' },
  joined: { fg: '#047857', bg: '#d1fae5' },
  left: { fg: '#b42318', bg: '#fdecea' },
  invitation: { fg: '#7a4b00', bg: '#fff4dd' },
  approval: { fg: '#0e7490', bg: '#cffafe' },
  permission: { fg: '#7e22ce', bg: '#f3e8ff' },
  organisation: { fg: '#334155', bg: '#e2e8f0' },
  account: { fg: '#9a3412', bg: '#ffedd5' },
};

export const isKind = (k: unknown): k is HistoryKind =>
  typeof k === 'string' && Object.prototype.hasOwnProperty.call(KIND_LABEL, k);

/** The "Kind of change" filter: each choice is one or more kinds (server filter `kind`). */
export const KIND_FILTERS: {
  value: string;
  label: string;
  kinds: HistoryKind[];
  /** source=outside: only changes made straight in the database */
  source?: 'outside';
}[] = [
  { value: '', label: 'All changes', kinds: [] },
  { value: 'people', label: 'People joined or left', kinds: ['joined', 'left'] },
  { value: 'roles', label: 'Roles and permissions', kinds: ['role', 'permission'] },
  { value: 'switches', label: 'Role switches', kinds: ['role_switch'] },
  { value: 'invitations', label: 'Invitations and approvals', kinds: ['invitation', 'approval'] },
  { value: 'organisation', label: 'Organisation set-up', kinds: ['organisation'] },
  { value: 'accounts', label: 'Accounts', kinds: ['account'] },
  { value: 'outside', label: 'Changed directly in the database', kinds: [], source: 'outside' },
];

export const sourceOf = (filter: string): 'outside' | null =>
  KIND_FILTERS.find((f) => f.value === filter)?.source ?? null;

export const kindsOf = (filter: string): HistoryKind[] =>
  KIND_FILTERS.find((f) => f.value === filter)?.kinds ?? [];

/** The "When" filter. */
export type Period = '7' | '30' | 'custom';

export const PERIOD_LABEL: Record<Period, string> = {
  '7': 'Last 7 days',
  '30': 'Last 30 days',
  custom: 'Custom',
};

const ymd = (d: Date): string => d.toISOString().slice(0, 10);

/** from / to ('YYYY-MM-DD', UTC) of a period; custom uses the typed dates. */
export function periodRange(
  period: Period,
  custom: { from: string; to: string },
  now: Date = new Date(),
): { from: string | null; to: string | null } {
  if (period === 'custom') return { from: custom.from || null, to: custom.to || null };
  const days = Number(period);
  return { from: ymd(new Date(now.getTime() - (days - 1) * 86_400_000)), to: null };
}

/** The UTC day of a 'YYYY-MM-DD HH:MM:SS' time. */
export const dayOf = (at: string): string => String(at ?? '').slice(0, 10);

/** "Today", "Yesterday" or "16 Oct 2026" (UTC days, like the times). */
export function dayLabel(day: string, now: Date = new Date()): string {
  const today = ymd(now);
  if (day === today) return 'Today';
  if (day === ymd(new Date(now.getTime() - 86_400_000))) return 'Yesterday';
  return dateText(day);
}

/** "07:57 UTC". */
export const clockText = (at: string): string => {
  const m = /\d{2}:\d{2}/.exec(String(at ?? '').slice(10));
  return m ? `${m[0]} UTC` : '';
};

/** Consecutive rows of the same day, newest first as the server sends them. */
export function groupByDay<T extends { at: string }>(items: T[]): { day: string; items: T[] }[] {
  const groups: { day: string; items: T[] }[] = [];
  for (const it of items) {
    const day = dayOf(it.at);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.items.push(it);
    else groups.push({ day, items: [it] });
  }
  return groups;
}

/** "AP" of "Ademola Plumptre", "Q" of "QA"; '' without letters. */
export function initialsOf(name: string): string {
  const words = String(name ?? '')
    .split(/[\s.@_-]+/)
    .filter((w) => /\p{L}/u.test(w));
  if (words.length === 0) return '';
  const first = words[0].charAt(0);
  const last = words.length > 1 ? words[words.length - 1].charAt(0) : '';
  return `${first}${last}`.toUpperCase();
}

/** A row from a server without the readable fields (older build): built from the old ones. */
export function readable(e: AccessHistoryItem): {
  kind: HistoryKind;
  who: HistoryWho;
  sentence: string;
  summary: string | null;
  reason: string | null;
  details: HistoryDetailGroup[];
} {
  const who: HistoryWho = e.who ?? {
    type: e.actor ? 'person' : e.source === 'database' ? 'outside' : 'system',
    name: e.actor?.name || (e.source === 'database' ? OUTSIDE_NAME : 'The system'),
    note: null,
  };
  return {
    kind: isKind(e.kind) ? e.kind : 'account',
    who,
    sentence:
      e.sentence ||
      [actionText(e.action), e.target_label ?? ''].filter((s) => s.trim() !== '').join(': '),
    summary: e.summary ?? null,
    reason: e.reason_text !== undefined ? e.reason_text : null,
    details: Array.isArray(e.details) ? e.details : [],
  };
}

/** Who of a change made straight in the database, not through PartnershipNet. */
export const OUTSIDE_NAME = 'Changed directly in the database';

/** Details longer than this show the first ones and "Show all N". */
export const DETAILS_SHOWN = 12;

/**
 * The first `limit` items across the groups (in order), the groups cut to them, and how many
 * were left out. Groups that end up empty are dropped.
 */
export function firstItems(
  groups: HistoryDetailGroup[],
  limit: number = DETAILS_SHOWN,
): { groups: HistoryDetailGroup[]; total: number; hidden: number } {
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  let left = limit;
  const out: HistoryDetailGroup[] = [];
  for (const g of groups) {
    if (left <= 0) break;
    const items = g.items.slice(0, left);
    left -= items.length;
    out.push({ ...g, items });
  }
  return { groups: out, total, hidden: Math.max(0, total - limit) };
}
